/* eslint-disable @typescript-eslint/no-explicit-any */
import { DataTableWrapper } from "@/Components/datatable/data-table";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/Components/ui/tooltip";
import { useContext, useState } from "react";
import { AppContext } from "@/App";
import PageLockProvider, { PageLockContext, PageName } from "@/Components/PageLockProvider";
import { columnDisplayNames, getRiskColumns } from "@/Components/Risks/risks_table/columns";
import { Risk } from "@/Components/Risks/Risk";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/Components/ui/dialog";
import Confirmation from "@/hooks/confirmation";
import { ColumnFiltersState } from "@tanstack/react-table";
import { formatDate } from "date-fns";
import { FilePlus2 } from "lucide-react";
import React from "react";
import { useLocation } from "react-router-dom";
import { Card } from "../Components/ui/card";
import HelpLink from "@/Components/ui/help-link";
import CSV_Importer from "@/Components/datatable/data_importer/csv_importer";
import RiskForm from "@/Components/Risks/risks_table/RiskForm";
import RiskPopup from "@/Components/Risks/risks_table/RiskPopup";
import { DeleteIcon, EditIcon } from "@/Components/ui/Icons/TooltipIcon";
import { getRatingCombination } from "@/lib/utils";
import WizardProvider, { WizardContext } from "@/Components/wizard/wizardProvider";
import { getBasicTourSteps } from "@/Components/Actions/actions_wizards/basic_page_tour";
import { RiskContext } from "@/Components/Risks/Risk";
import { useResponsiveZoom } from "@/hooks/use-responsive-zoom";
import { useIsMobile } from "@/hooks/use-mobile";

export type RiskColumnVisibility = {
    riskId: boolean
    workstream: boolean
    workPackage: boolean
    risk: boolean
    mitigation: boolean
    rating: boolean
    status: boolean
    date: boolean
    raisedBy: boolean
    probability: boolean
    impact: boolean
    owner: boolean
    notes: boolean
    displayOnDashboard: boolean
    displayOnStatusReport: boolean
    seen: boolean
    acknowledged: boolean
}

function RisksPage() {
    const app = useContext(AppContext);
    const isMobile = useIsMobile();
    const lock = useContext(PageLockContext);
    const wizard = useContext(WizardContext);
    const zoomLevel = useResponsiveZoom();

    const [demoMode, setDemoMode] = useState<boolean>(false);
    const useInlineEditing = app.applicationUser?.userSettings?.inlineTableEditing ?? false;

    const [dialogOpen, setDialogOpen] = React.useState(false);
    const [loading, setLoading] = useState<boolean>(true);

    const locationState = useLocation().state as { passedColumnFilters?: (ColumnFiltersState & { riskId?: number }) | undefined };
    const passedColumnFilters = locationState || undefined;
    const [clearPassedColumnFilters, setClearPassedColumnFilters] = useState<boolean>(false);

    const [dialogTitle, setDialogTitle] = useState("Create New Risk");
    const [dialogTitleBarActions, setDialogTitleBarActions] = useState<JSX.Element | null>(null);
    const [dialogContent, setDialogContent] = useState<JSX.Element | null>(null);

    const users = app.teamMembers.concat(app.projectManagers).concat(app.sponsors);

    const [riskTableContent, setRiskTableContent] = useState<Risk[]>([]);
    const [riskTableDemoContent, setRiskTableDemoContent] = useState<Risk[]>([]);

    const [rowSelection, setRowSelection] = useState({});

    const [importedRows, setImportedRows] = useState<any[]>([]);

    React.useEffect(() => {
        if (importedRows.length > 0) {
            const newRisks = importedRows.map((row) => {
                const newRisk: Risk = {
                    ...row,
                    riskId: 0,
                    projectId: app.selectedProject?.id || 0,
                    userId: users.find(u => u.displayName === row.owner)?.id || "",
                    workPackageId: app.workPackageOptions?.flatMap(w => w.workPackageNames).find(wp => wp.workPackageName === row.workPackage)?.workPackageId || 0,
                    workstreamId: app.workPackageOptions?.[0]?.workstreamId || 0,
                };
                return newRisk;
            });

            saveRisks(newRisks);
            setImportedRows([]);
        }
    }, [importedRows]);

    const importDataType: { name: string, type: any, example: string }[] = [
        { name: "workPackage", type: String, example: "WP-1" },
        { name: "risk", type: String, example: "Risk description" },
        { name: "mitigation", type: String, example: "Mitigation steps" },
        { name: "status", type: String, example: "Open" },
        { name: "date", type: Date, example: "2026-01-15" },
        { name: "raisedBy", type: String, example: "John Doe" },
        { name: "probability", type: String, example: "High" },
        { name: "impact", type: String, example: "High" },
        { name: "rating", type: String, example: "High" },
        { name: "owner", type: String, example: "Jane Smith" },
        { name: "notes", type: String, example: "Additional notes" },
        { name: "displayOnDashboard", type: Boolean, example: "true" },
        { name: "displayOnStatusReport", type: Boolean, example: "true" }
    ];

    React.useEffect(() => {
        if (app.authLevel !== 3 && !lock.viewOnly) { // viewer cant see this page. Sponsor can see it but not edit it
            const down = (e: KeyboardEvent) => {
                if (e.key === "r" && (e.metaKey || e.ctrlKey)) {
                    e.preventDefault();
                    addRisk();
                }
            }
            window.addEventListener("keydown", down)
            return () => window.removeEventListener("keydown", down)
        }
        populateDemoData();
    }, []);

    React.useEffect(() => {
        refreshRisks();
    }, [app.selectedProject]);

    // Keep track of previous risk table content for comparison
    const prevRiskTableContentRef = React.useRef<Risk[]>([]);

    React.useEffect(() => {
        // Skip on initial load or when refreshing all risks
        if (prevRiskTableContentRef.current.length === 0 ||
            loading ||
            riskTableContent.length !== prevRiskTableContentRef.current.length) {
            prevRiskTableContentRef.current = [...riskTableContent];
            return;
        }

        // Find updated risks by comparing with previous state
        const updatedRisks = riskTableContent.filter((currentRisk) => {
            const prevRisk = prevRiskTableContentRef.current.find(r => r.riskId === currentRisk.riskId);
            if (!prevRisk) return false;

            // Compare all properties to detect changes
            return JSON.stringify(prevRisk) !== JSON.stringify(currentRisk);
        });

        // Save each updated risk to the server
        updatedRisks.forEach(risk => {
            if (risk.riskId > 0) { // Only update existing risks, not new ones
                updateRisk(risk);
            }
        });

        // Update ref with current state
        prevRiskTableContentRef.current = [...riskTableContent];
    }, [riskTableContent]);

    function showFormDialog(selectedRisk?: Risk) {
        const action = selectedRisk ? (
            <div onClick={(e) => {
                e.stopPropagation();
                if (selectedRisk.riskId) {
                    confirmDeleteRisks([selectedRisk.riskId]);
                }
            }}
                className="p-2 rounded-md cursor-pointer hover:bg-red-600">
                <DeleteIcon className={`${isMobile ? "w-8 h-8" : "w-4 h-4"}`} />
            </div>
        ) : null;

        setDialogTitleBarActions(action);

        setDialogTitle(selectedRisk ? "Edit Risk" : "Create New Risk");

        setDialogContent(
            <RiskForm
                saveRisk={saveRisk}
                risk={selectedRisk}
            />
        );
        setDialogOpen(true);
    }

    function showPopup(selectedRisk: Risk) {
        const actions = (
            <div className="flex flex-row">
                <div onClick={() => {
                    showFormDialog(selectedRisk);
                }}
                    className="ml-2 p-2 rounded-md cursor-pointer hover:bg-blue-600"
                >
                    <EditIcon className={`${isMobile ? "w-8 h-8" : "w-4 h-4"}`} />
                </div>
                <div onClick={(e) => {
                    e.stopPropagation();
                    if (selectedRisk.riskId) {
                        confirmDeleteRisks([selectedRisk.riskId]);
                    }
                }}
                    className="p-2 rounded-md cursor-pointer hover:bg-red-600">
                    <DeleteIcon className={`${isMobile ? "w-8 h-8" : "w-4 h-4"}`} />
                </div>
            </div>
        );

        setDialogTitle("Risk Details");
        setDialogTitleBarActions(
            app.authLevel !== 3 && !lock.viewOnly ? actions : null
        );
        setDialogContent(
            <RiskPopup
                risk={selectedRisk}
            />
        );
        setDialogOpen(true);
    }

    function refreshRisks() {
        if (app.selectedProject) {
            setLoading(true);
            document.body.style.cursor = 'wait';
            // fetch risks for project
            fetch("Raids/Risks/" + app.selectedProject.id)
                .then((response) => response.json())
                .then((data) => {
                    setRiskTableContent(data);
                    setLoading(false);
                }
                ).catch((error) => {
                    app.showActionNotification("Error", "Error fetching actions: " + error.message, "destructive");
                })
                .finally(() => {
                    document.body.style.cursor = 'default';
                });
        }
    }

    function confirmDeleteRisks(riskIds: number[]) {
        setDialogTitleBarActions(null);
        setDialogTitle(riskIds.length > 1 ? "Are you sure you want to delete these risks?" : "Are you sure you want to delete this risk?");
        setDialogContent(<Confirmation onConfirm={() => { deleteRisks(riskIds); setDialogOpen(false); }} onCancel={() => setDialogOpen(false)} confirmColour="bg-chart-red" />);
        setDialogOpen(true);
    }

    function editRisk(id: number) {
        if (demoMode) {
            showFormDialog(riskTableDemoContent.find((a) => a.riskId === id));
            return;
        }
        showFormDialog(riskTableContent.find((risk) => risk.riskId === id) || undefined);
    }

    function loadColumnVisibility(): RiskColumnVisibility {
        const localStorageValue = localStorage.getItem(app.applicationUser?.id + "_Risks_columnVisibility");
        if (localStorageValue) {
            const visibiltyValues: { columnId: string, isVisible: boolean }[] = JSON.parse(localStorageValue);
            return ({
                riskId: visibiltyValues.find((v) => v.columnId === "riskId")?.isVisible ?? false,
                workstream: visibiltyValues.find((v) => v.columnId === "workstream")?.isVisible ?? false,
                workPackage: visibiltyValues.find((v) => v.columnId === "workPackage")?.isVisible ?? true,
                risk: visibiltyValues.find((v) => v.columnId === "risk")?.isVisible ?? true,
                mitigation: visibiltyValues.find((v) => v.columnId === "mitigation")?.isVisible ?? true,
                rating: visibiltyValues.find((v) => v.columnId === "rating")?.isVisible ?? true,
                status: visibiltyValues.find((v) => v.columnId === "status")?.isVisible ?? true,
                date: visibiltyValues.find((v) => v.columnId === "date")?.isVisible ?? true,
                raisedBy: visibiltyValues.find((v) => v.columnId === "raisedBy")?.isVisible ?? false,
                probability: visibiltyValues.find((v) => v.columnId === "probability")?.isVisible ?? false,
                impact: visibiltyValues.find((v) => v.columnId === "impact")?.isVisible ?? false,
                owner: visibiltyValues.find((v) => v.columnId === "owner")?.isVisible ?? true,
                notes: visibiltyValues.find((v) => v.columnId === "notes")?.isVisible ?? false,
                displayOnStatusReport: visibiltyValues.find((v) => v.columnId === "displayOnStatusReport")?.isVisible ?? false,
                displayOnDashboard: visibiltyValues.find((v) => v.columnId === "displayOnDashboard")?.isVisible ?? false,
                seen: visibiltyValues.find((v) => v.columnId === "seen")?.isVisible ?? false,
                acknowledged: visibiltyValues.find((v) => v.columnId === "acknowledged")?.isVisible ?? false,
            })
        }
        return {
            riskId: false,
            workstream: false,
            workPackage: true,
            risk: true,
            mitigation: true,
            rating: true,
            status: true,
            date: true,
            raisedBy: false,
            probability: false,
            impact: false,
            owner: true,
            notes: false,
            displayOnStatusReport: false,
            displayOnDashboard: false,
            seen: false,
            acknowledged: false,
        }
    }

    function addRisk() {
        setClearPassedColumnFilters(true);
        if (useInlineEditing) {
            const newRisk: Risk = {
                riskId: 0,
                projectId: app.selectedProject?.id || 0,
                workstream: app.workPackageOptions?.[0]?.workstreamName || "",
                workPackage: app.workPackageOptions?.[0]?.workPackageNames[0]?.workPackageName || "",
                workstreamId: app.workPackageOptions?.[0]?.workstreamId || 0, // default to first workstream
                workPackageId: app.workPackageOptions?.[0]?.workPackageNames[0]?.workPackageId || 0, // default to first work package
                risk: "New Risk",
                mitigation: "Mitigation",
                probability: "Medium",
                impact: "Medium",
                rating: "Medium",
                status: "Open",
                date: new Date(),
                owner: "",
                raisedBy: "",
                notes: "",
                displayOnStatusReport: false,
                displayOnDashboard: false,
                seen: false,
                acknowledged: false,
                userId: ""
            };

            saveRisk(newRisk);
        } else {
            showFormDialog();
        }
    }

    function saveRisk(risk: Risk) {
        if (risk.riskId !== 0) {
            updateRisk(risk);
            return;
        }
        document.body.style.cursor = 'wait';
        if (demoMode) {
            const newRisk = { ...risk, riskId: Math.floor(Math.random() * 1000000) };
            setRiskTableDemoContent((prev) => [newRisk, ...prev]);
            app.showActionNotification("Success", "Risk Added Successfully!", "success");
            document.body.style.cursor = 'default';
            return;
        }
        fetch("/Raids/Risk", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(risk),
        })
            .then((response) => response.json())
            .then((data) => {
                setRiskTableContent((prev) => [data, ...prev]); // add new risk to the top of the table using functional update
                app.showActionNotification("Success", "Risk Added Successfully!", "success");
            })
            .catch((error) => {
                app.showActionNotification("Error", "Error adding risk: " + error.message, "destructive");
            })
            .finally(() => {
                document.body.style.cursor = 'default';
            });
    }

    function saveRisks(risks: Risk[]) {
        document.body.style.cursor = 'wait';
        if (demoMode) {
            const newRisks = risks.map(risk => ({ ...risk, riskId: Math.floor(Math.random() * 1000000) }));
            setRiskTableDemoContent((prev) => [...newRisks, ...prev]);
            app.showActionNotification("Success", `${risks.length} Risks added from CSV`, "success");
            document.body.style.cursor = 'default';
            return;
        }
        fetch("/Raids/Risks", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(risks),
        })
            .then((response) => response.json())
            .then((data) => {
                setRiskTableContent((prev) => [...data, ...prev]);
                app.showActionNotification("Success", `${risks.length} Risks added from CSV`, "success");
            })
            .catch((error) => {
                app.showActionNotification("Error", "Error adding risks: " + error.message, "destructive");
            })
            .finally(() => {
                document.body.style.cursor = 'default';
            });
    }

    function updateRisk(risk: Risk) {
        document.body.style.cursor = 'wait';

        if (useInlineEditing) {
            risk.rating = getRatingCombination(risk.probability as "High" | "Medium" | "Low", risk.impact as "High" | "Medium" | "Low") as "High" | "Medium" | "Low";
        }
        if (demoMode) {
            const newRisk = { ...risk, riskId: Math.floor(Math.random() * 1000000) };
            setRiskTableDemoContent((prev) =>
                prev.map((r) => (r.riskId === risk.riskId ? newRisk : r))
            );
            app.showActionNotification("Success", "Risk Updated Successfully!", "success");
            document.body.style.cursor = 'default';
            return;
        }
        fetch(`/Raids/Risk/${risk.riskId}`, {
            method: "PUT",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                ...risk,
                date: formatDate(risk.date, "yyyy-MM-dd"),
            }),
        })
            .then((response) => {
                if (response.ok) {
                    if (!useInlineEditing) {
                        // If not using inline editing, update the specific risk in state
                        setRiskTableContent((prev) =>
                            prev.map((r) => (r.riskId === risk.riskId ? risk : r))
                        );
                    }
                    app.showActionNotification("Success", "Risk Updated Successfully!", "success");
                } else {
                    app.showActionNotification("Error", "Error updating risk: " + response.statusText, "destructive");
                }
            })
            .catch((error) => {
                app.showActionNotification("Error", "Error updating risk: " + error.message, "destructive");
            })
            .finally(() => {
                document.body.style.cursor = 'default';
            });
    }

    function deleteRisks(ids: number[]) {
        document.body.style.cursor = 'wait';
        if (demoMode) {
            setRiskTableDemoContent((prev) => prev.filter((r) => !ids.includes(r.riskId)));
            app.showActionNotification("Success", "Risk Deleted", "success");
            document.body.style.cursor = 'default';
            return;
        }
        fetch(`Raids/Risks`, {
            method: "DELETE",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(ids),
        })
            .then(() => {
                // Use functional update to avoid stale state issues
                setRiskTableContent(prev =>
                    prev.filter((risk) => !ids.includes(risk.riskId))
                );
                app.showActionNotification("Success", "Risk Deleted", "success");
            })
            .catch((error) => {
                app.showActionNotification("Error", `Error deleting risk: ${error.message}`, "destructive");
            })
            .finally(() => {
                document.body.style.cursor = 'default';
            });
    }

    function getGroupActions() {
        const groupActions = app.authLevel > 3 ? [{
            name: "Delete",
            action: (selectedRows: any[]) => {
                confirmDeleteRisks(selectedRows.map((row) => row.riskId));
            },
        }] : [];

        groupActions.push(
            {
                name: "Set Status to Open",
                action: (selectedRows: any[]) => {
                    const riskStatus = selectedRows.map((row) => ({ "id": row.riskId, "status": "Open" as "Closed" | "Cancelled" | "Open" }));
                    setStatus(riskStatus);
                },
            },
            {
                name: "Set Status to Closed",
                action: (selectedRows: any[]) => {
                    const riskStatus = selectedRows.map((row) => ({ "id": row.riskId, "status": "Closed" as "Closed" | "Cancelled" | "Open" }));
                    setStatus(riskStatus);
                },
            },
            {
                name: "Set Status to Cancelled",
                action: (selectedRows: any[]) => {
                    const riskStatus = selectedRows.map((row) => ({ "id": row.riskId, "status": "Cancelled" as "Closed" | "Cancelled" | "Open" }));
                    setStatus(riskStatus);
                },
            });
        return groupActions;
    }

    function setStatus(riskStatus: { id: number, status: "Closed" | "Cancelled" | "Open" }[]) {
        document.body.style.cursor = 'wait';
        if (demoMode) {
            setRiskTableDemoContent(riskTableDemoContent.map((risk) => {
                const updatedRisk = riskStatus.find((rs) => rs.id === risk.riskId);
                if (updatedRisk) {
                    risk.status = updatedRisk.status;
                }
                return risk;
            }));
            app.showActionNotification("Success", "Status Updated", "success");
            document.body.style.cursor = 'default';
            return;
        }
        fetch("Raids/RiskStatus", {
            method: "PUT",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(riskStatus),
        })
            .then(() => {
                setRiskTableContent(riskTableContent.map((risk) => {
                    const updatedRisk = riskStatus.find((rs) => rs.id === risk.riskId);
                    if (updatedRisk) {
                        risk.status = updatedRisk.status;
                    }
                    return risk;
                }));
                app.showActionNotification("Success", "Status Updated", "success");
            })
            .catch((error) => {
                app.showActionNotification("Error", `Error updating status: ${error.message}`, "destructive");
            })
            .finally(() => {
                document.body.style.cursor = 'default';
            });
    }

    function populateDemoData() {
        const demoRisks: Risk[] = [];
        const owners = users.length > 0 ? users.map(u => u.displayName) : ["User 1", "User 2", "User 3"];
        const workPackages = app.workPackageOptions?.flatMap(w => w.workPackageNames).map(wp => wp.workPackageName) || ["WP-1", "WP-2", "WP-3"];
        for (let i = 0; i < 20; i++) {
            const randomOwner = owners[Math.floor(Math.random() * owners.length)];
            const randomRaisedBy = owners[Math.floor(Math.random() * owners.length)];
            const randomWorkPackage = workPackages[Math.floor(Math.random() * workPackages.length)];
            const impact = Math.random() < 0.33 ? "Low" : (Math.random() < 0.5 ? "Medium" : "High");
            const probability = Math.random() < 0.33 ? "Low" : (Math.random() < 0.5 ? "Medium" : "High");
            const newRisk: Risk = {
                riskId: i + 1,
                date: new Date(new Date().setDate(new Date().getDate() - Math.floor(Math.random() * 30))),
                projectId: app.selectedProject?.id || 0,
                workstream: app.workPackageOptions?.[0]?.workstreamName || "Default Stream",
                workPackage: randomWorkPackage,
                workstreamId: app.workPackageOptions?.[0]?.workstreamId || 0,
                workPackageId: app.workPackageOptions?.flatMap(w => w.workPackageNames).find(wp => wp.workPackageName === randomWorkPackage)?.workPackageId || 0,
                raisedBy: randomRaisedBy,
                risk: `This is a demo risk number ${i + 1}`,
                mitigation: `These are the mitigation steps for risk number ${i + 1}`,
                probability: probability,
                impact: impact,
                rating: getRatingCombination(
                    impact,
                    probability
                ) as "Low" | "Medium" | "High",
                status: Math.random() < 0.7 ? "Open" : (Math.random() < 0.5 ? "Closed" : "Cancelled"),
                owner: randomOwner,
                userId: users.find(u => u.displayName === randomOwner)?.id?.toString() || "",
                notes: Math.random() < 0.5 ? `These are some notes for action item number ${i + 1}` : "",
                displayOnDashboard: Math.random() < 0.5,
                displayOnStatusReport: Math.random() < 0.5,
                seen: Math.random() < 0.5,
                acknowledged: Math.random() < 0.5,
            };
            demoRisks.push(newRisk);
        }
        setRiskTableDemoContent(demoRisks);
    }

    const demoButton = (
        <TooltipProvider delayDuration={1}>
            <Tooltip>
                <TooltipTrigger asChild>
                    <button id="exitDemoMode" className="rounded-md text-white bg-red-500 h-min px-2 my-auto" onClick={() => setDemoMode(false)}>Exit Demo Mode</button>
                </TooltipTrigger>
                <TooltipContent className="flex flex-col bg-background">
                    <div className="flex flex-row text-muted-foreground">
                        <p>
                            Demo mode allows you to try out the Actions page without making any actual changes to the data.
                        </p>
                        <p>
                            The table will be populated with some sample data and you can try out adding, editing, deleting and exporting Actions.
                        </p>
                    </div>
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );

    const contextValue = {
        dialogOpen,
        setDialogOpen,
        riskTableDemoContent,
        setRiskTableDemoContent,
        showPopup,
        demoMode,
        setDemoMode,
        rowSelection,
        setRowSelection,
        addRisk,
    }

    if (isMobile) {
        return (
            <RiskContext.Provider value={contextValue}>
                <Card className="mb-8 relative" style={{ zoom: zoomLevel }}>
                    {wizard && wizard.getWizardPointer()}
                    <div className="flex flex-row bg-table-risks justify-between px-2 rounded-t-lg">
                        <div className="flex flex-row">
                            <h1 className="text-4xl my-auto p-2">Risks</h1>
                            <div className="my-auto" onClick={refreshRisks}>{lock.getRefreshControl()}</div>
                        </div>
                        <div className="flex flex-row">
                            {app.authLevel !== 3 && !lock.viewOnly ? ( // viewer cant see this page. Sponsor can see it but not edit it
                                <div className="flex justify-end">
                                    <TooltipProvider delayDuration={1}>
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <div
                                                    id="addNewRisk"
                                                    onClick={() => addRisk()}
                                                    className="rounded-full bg-table-risks border border-white text-table-header-foreground p-2 hover:cursor-pointer m-2"
                                                >
                                                    <FilePlus2 className="w-8 h-8" />
                                                </div>
                                            </TooltipTrigger>
                                            <TooltipContent className="flex flex-col bg-background">
                                                <div className="flex flex-row text-muted-foreground place-content-between">
                                                    <span className="p-2">Create New Risk</span>
                                                </div>
                                            </TooltipContent>
                                        </Tooltip>
                                    </TooltipProvider>
                                </div>
                            ) : <div></div>}
                        </div>
                        <div className="flex flex-row absolute top-0 right-2 -translate-y-[130%]">{app.authLevel !== 3 && lock.getLockControl()}</div>
                    </div>
                    <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                        <DialogContent id="risk-dialog" className="flex flex-col w-[95%]" style={{ zoom: zoomLevel }} onInteractOutside={(e) => {
                            if (demoMode) {
                                e.preventDefault();
                            }
                        }}>
                            <DialogHeader>
                                <DialogTitle><div className="flex"><div className="text-4xl my-auto mr-2">{dialogTitle}</div>{dialogTitleBarActions}</div></DialogTitle>
                            </DialogHeader>
                            {dialogContent}
                        </DialogContent>
                    </Dialog>
                    <DataTableWrapper
                        columns={getRiskColumns(
                            (ids: number[]) => confirmDeleteRisks(ids),
                            (id: number) => editRisk(id)
                        )}
                        data={demoMode ? riskTableDemoContent : riskTableContent}
                        setData={demoMode ? setRiskTableDemoContent : setRiskTableContent}
                        loading={loading}
                        viewOnly={app.authLevel === 3 || lock.viewOnly}
                        header_colour="bg-table-risks"
                        table_title="Risks"
                        columnVisibility={loadColumnVisibility()}
                        passedColumnFilters={!clearPassedColumnFilters ? passedColumnFilters as ColumnFiltersState : undefined}
                        groupActions={getGroupActions()}
                        columnDisplayNames={columnDisplayNames}
                        onRowClick={!useInlineEditing ? (row) => { showPopup((row.original as Risk)); } : undefined}
                        rowSelection={rowSelection}
                        setRowSelection={setRowSelection}
                    />
                    <div className="flex flex-row w-min ml-auto">{wizard.getTourControls()}</div>
                </Card>
            </RiskContext.Provider>
        );
    }

    return (
        <RiskContext.Provider value={contextValue}>
            <Card className="p-2 m-2 relative" style={{ zoom: zoomLevel }}>
                {wizard && wizard.getWizardPointer()}
                <div className="flex flex-row justify-between">
                    <div className="flex flex-row"><h1 className="text-2xl my-auto p-2">Risks</h1><div className="my-auto" onClick={refreshRisks}>{lock.getRefreshControl()}</div></div>
                    <div className="flex flex-row">{app.authLevel !== 3 && lock.getLockControl()}<HelpLink page="risk" />{demoMode && demoButton}</div>
                    {app.authLevel !== 3 && !lock.viewOnly ? ( // viewer cant see this page. Sponsor can see it but not edit it
                        <div className="flex justify-end">
                            <TooltipProvider delayDuration={1}>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <div
                                            id="addNewRisk"
                                            onClick={() => addRisk()}
                                            className="rounded-full bg-table-risks text-table-header-foreground w-min p-2 hover:cursor-pointer m-2"
                                        >
                                            <FilePlus2 />
                                        </div>
                                    </TooltipTrigger>
                                    <TooltipContent className="flex flex-col bg-background">
                                        <div className="flex flex-row text-muted-foreground place-content-between">
                                            <span className="p-2">Create New Risk</span>
                                        </div>
                                    </TooltipContent>
                                </Tooltip>
                            </TooltipProvider>
                            {<CSV_Importer propertyTypes={importDataType} setNewRows={setImportedRows} bgColor="bg-table-risks" title="Import Risks from CSV" />}
                        </div>
                    ) : <div></div>}
                </div>
                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                    <DialogContent id="risk-dialog" className="flex flex-col max-w-fit" onInteractOutside={(e) => {
                        if (demoMode) {
                            e.preventDefault();
                        }
                    }}>
                        <DialogHeader>
                            <DialogTitle><div className="flex"><div className="my-auto mr-2">{dialogTitle}</div>{dialogTitleBarActions}</div></DialogTitle>
                        </DialogHeader>
                        {dialogContent}
                    </DialogContent>
                </Dialog>
                <DataTableWrapper
                    columns={getRiskColumns(
                        (ids: number[]) => confirmDeleteRisks(ids),
                        (id: number) => editRisk(id)
                    )}
                    data={demoMode ? riskTableDemoContent : riskTableContent}
                    setData={demoMode ? setRiskTableDemoContent : setRiskTableContent}
                    loading={loading}
                    viewOnly={app.authLevel === 3 || lock.viewOnly}
                    header_colour="bg-table-risks"
                    table_title="Risks"
                    columnVisibility={loadColumnVisibility()}
                    passedColumnFilters={!clearPassedColumnFilters ? passedColumnFilters as ColumnFiltersState : undefined}
                    groupActions={getGroupActions()}
                    columnDisplayNames={columnDisplayNames}
                    onRowClick={!useInlineEditing ? (row) => { showPopup((row.original as Risk)); } : undefined}
                    rowSelection={rowSelection}
                    setRowSelection={setRowSelection}
                />
                <div className="flex flex-row w-min ml-auto">{wizard.getTourControls()}</div>
            </Card>
        </RiskContext.Provider>
    );
}

function Risks() {
    const app = useContext(AppContext);
    return <PageLockProvider pageName={PageName.Risks} userId={app.applicationUser?.id as string} projectId={app.selectedProject?.id as number} >
        <WizardProvider wizardSteps={getBasicTourSteps()} >
            <RisksPage />
        </WizardProvider>
    </PageLockProvider>;
}

export default Risks;